import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sparkles,
  Home,
  Image,
  BarChart3,
  Settings,
  CreditCard,
  LogOut,
  Crown,
  Infinity,
  Zap,
  ChevronUp,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription, SUBSCRIPTION_TIERS } from "@/hooks/useSubscription";
import { toast } from "sonner";
import UserAvatar from "@/components/UserAvatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type TabType = "dashboard" | "thumbnails" | "analytics" | "settings" | "billing";

interface DashboardSidebarProps {
  activeTab: TabType;
  generationsLeft?: number;
}

const DashboardSidebar = ({ activeTab, generationsLeft = 5 }: DashboardSidebarProps) => {
  const { user, signOut } = useAuth();
  const { subscribed, tier, openCustomerPortal, loading: subLoading } = useSubscription();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out successfully");
    navigate("/");
  };

  const sidebarLinks = [
    { id: "dashboard" as const, icon: Home, name: "Dashboard", href: "/dashboard" },
    { id: "thumbnails" as const, icon: Image, name: "My Thumbnails", href: "/my-thumbnails" },
    { id: "analytics" as const, icon: BarChart3, name: "Analytics", href: "/analytics" },
  ];

  return (
    <aside className="w-64 border-r border-border bg-card hidden lg:flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl gradient-bg flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="text-xl font-bold gradient-text">ThumbGenius</span>
        </Link>
      </div>

      {/* Nav Links */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {sidebarLinks.map((link) => (
            <li key={link.id}>
              <Link
                to={link.href}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  activeTab === link.id
                    ? "bg-primary/10 text-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                <link.icon className="w-5 h-5" />
                <span className="font-medium">{link.name}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Plan Status Card */}
      <div className="p-4">
        {subscribed ? (
          <div className={`p-4 rounded-xl border ${
            tier === 'enterprise' 
              ? 'bg-gradient-to-br from-amber-500/20 to-amber-500/5 border-amber-500/30' 
              : tier === 'pro'
              ? 'bg-gradient-to-br from-purple-500/20 to-purple-500/5 border-purple-500/30'
              : 'bg-gradient-to-br from-green-500/20 to-green-500/5 border-green-500/30'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <Crown className={`w-5 h-5 ${
                tier === 'enterprise' ? 'text-amber-500' : tier === 'pro' ? 'text-purple-500' : 'text-green-500'
              }`} />
              <span className={`font-semibold ${
                tier === 'enterprise' ? 'text-amber-400' : tier === 'pro' ? 'text-purple-400' : 'text-green-400'
              }`}>
                {tier ? SUBSCRIPTION_TIERS[tier].name : 'Premium Plan'}
              </span>
              {(tier === 'pro' || tier === 'enterprise') && (
                <span className={`ml-auto px-2 py-0.5 text-xs font-bold rounded-full ${
                  tier === 'enterprise' 
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                    : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                }`}>
                  VIP
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Infinity className="w-4 h-4" />
              <span>Unlimited generations</span>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className={`w-full ${
                tier === 'enterprise' 
                  ? 'border-amber-500/30 hover:bg-amber-500/10' 
                  : tier === 'pro'
                  ? 'border-purple-500/30 hover:bg-purple-500/10'
                  : 'border-green-500/30 hover:bg-green-500/10'
              }`}
              onClick={() => openCustomerPortal()}
              disabled={subLoading}
            >
              Manage Subscription
            </Button>
          </div>
        ) : (
          <div className="p-4 rounded-xl bg-gradient-to-br from-primary/20 via-brand-pink/10 to-primary/5 border border-primary/30">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-5 h-5 text-amber-400" />
              <span className="font-bold text-sm">
                {generationsLeft <= 1 ? "⚠️ Limite atteinte !" : "Passez à la vitesse supérieure"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              {3 - generationsLeft} / 3 générations utilisées
            </p>
            <div className="w-full h-2 rounded-full bg-secondary mb-2">
              <div 
                className={`h-full rounded-full transition-all ${
                  generationsLeft <= 1
                    ? 'bg-gradient-to-r from-orange-500 to-red-500'
                    : 'gradient-bg'
                }`}
                style={{ width: `${((3 - generationsLeft) / 3) * 100}%` }}
              />
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">
              Débloquez les miniatures illimitées et l'export HD ✨
            </p>
            <Link to="/pricing">
              <Button 
                variant="gradient" 
                size="sm" 
                className="w-full btn-shimmer bg-gradient-to-r from-purple-600 via-violet-500 to-fuchsia-500 border-0 gap-2 font-bold shadow-[0_0_20px_hsl(262,83%,58%,0.4)]"
              >
                <Crown className="w-4 h-4" />
                Upgrade to Pro
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* User Profile Popover */}
      <div className="p-4 border-t border-border">
        <Popover>
          <PopoverTrigger asChild>
            <button className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-secondary transition-colors cursor-pointer">
              <UserAvatar 
                avatarUrl={user?.user_metadata?.avatar_url}
                name={user?.user_metadata?.full_name}
                tier={tier}
                size="md"
              />
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm truncate">
                    {user?.user_metadata?.full_name || "User"}
                  </p>
                  {(tier === 'pro' || tier === 'enterprise') && (
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${
                      tier === 'enterprise' 
                        ? 'bg-amber-500/20 text-amber-400' 
                        : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      VIP
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent 
            side="top" 
            align="start" 
            sideOffset={8}
            className="w-56 p-1.5 bg-card border border-border rounded-xl shadow-xl"
          >
            <div className="space-y-0.5">
              <Link
                to="/settings"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  activeTab === 'settings'
                    ? 'bg-primary/10 text-foreground'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`}
              >
                <Settings className="w-4 h-4" />
                <span>Paramètres du compte</span>
              </Link>
              <Link
                to="/billing"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  activeTab === 'billing'
                    ? 'bg-primary/10 text-foreground'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`}
              >
                <CreditCard className="w-4 h-4" />
                <span>Facturation</span>
              </Link>
              <div className="h-px bg-border my-1" />
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Déconnexion</span>
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </aside>
  );
};

export default DashboardSidebar;
